/**
 * Document view component, tweaked from source.
 * @module components/theme/View/DefaultView
 */

import { useEffect, useRef, useState } from 'react';

import config from '@plone/volto/registry';
import { getSchema } from '@plone/volto/actions/schema/schema';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import { Button } from '@plone/components';
import { hasBlocksData } from '@plone/volto/helpers/Blocks/Blocks';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { useDispatch } from 'react-redux';
import cx from 'classnames';
import isEqual from 'lodash/isEqual';
import SlotRenderer from '@plone/volto/components/theme/SlotRenderer/SlotRenderer';
import type { QubicaContentProps } from '@redturtle/qubicaamf-theme/components/View/Common/utils';
import Image from '@plone/volto/components/theme/Image/Image';

import styles from './styles.module.css';
import type { Brain, Expanders } from '@plone/types';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    // dataLayer?: Record<string, any>[];
  }
}

export interface CategoriesBehavior {
  blog_tags: Array<string>;
}
export interface MacroCategoriesBehavior {
  news_macro_type: Array<QubicaContentProps>;
}

export interface NewsMacroTypeExpander extends Brain, CategoriesBehavior {}

export interface BlogExpanders extends Expanders {
  blog_summary: {
    last_5_news?: NewsItem[];
    news_macro_types?: NewsMacroTypeExpander[];
  };
}

export interface NewsItem
  extends QubicaContentProps,
    CategoriesBehavior,
    MacroCategoriesBehavior {
  news_collapsed_view: boolean;
  news_macro_type_info: Brain;
}
export interface Testimonial
  extends QubicaContentProps,
    CategoriesBehavior,
    MacroCategoriesBehavior {
  payoff: string;
  person_name: string;
  person_position: string;
  location: string;
  vending_place: string;
  icon: object;
  referenced_product: Array<Brain>;
}
export type TestimonialOrNews = NewsItem | Testimonial;

export function isNews(content: Props['content']): content is NewsItem {
  return content?.['@type'] === 'News Item';
}
export function isTestimonial(
  content: Props['content'],
): content is Testimonial {
  return content?.['@type'] === 'Testimonial';
}
function isCollapsedNewsItem(content: Props['content']) {
  return isNews(content) && content?.news_collapsed_view;
}

type Props = { content: TestimonialOrNews; location: any };

const NewsTestimonialView = (props: Props) => {
  const { content, location } = props;
  const path = getBaseUrl(location?.pathname || '');
  const dispatch = useDispatch();

  // TL;DR: There is a flash of the non block-based view because of the reset
  // of the content on route change. Subscribing to the content change at this
  // level has nasty implications, so we can't watch the Redux state for loaded
  // content flag here (because it forces an additional component update)
  // Instead, we can watch if the content is "empty", but this has a drawback
  // since the locking mechanism inserts a `lock` key before the content is there.
  // So "empty" means `content` is present, but only with a `lock` key, thus the next
  // ugly condition comes to life
  const contentLoaded = content && !isEqual(Object.keys(content), ['lock']);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(!isCollapsedNewsItem(content));
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      const height = el.scrollHeight;
      // Get the root element and then
      // Rrtrieve the current value of a CSS variable
      const root = document.documentElement;
      const MAX_HEIGHT = getComputedStyle(root).getPropertyValue(
        '--qamf-news-blog-collapse-max-height',
      );
      setIsOverflowing(height > parseInt(MAX_HEIGHT.replace('px', '')));
    });

    observer.observe(el);

    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    content?.['@type'] &&
      !hasBlocksData(content) &&
      dispatch(getSchema(content['@type'], location.pathname));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Container =
    config.getComponent({ name: 'Container' }).component || 'div';

  const testimonial_info =
    isTestimonial(content) &&
    [content.vending_place, content.person_position]
      .filter(Boolean)
      .join(' - ');

  // If the content is not yet loaded, then do not show anything
  return contentLoaded ? (
    <Container id="page-document" className={styles.container}>
      <div className={styles.contentWrapper}>
        <div
          className={cx(styles.collapsibleWrapper, {
            [styles.collapsed]: isOverflowing && !isExpanded,
          })}
          ref={containerRef}
        >
          {isTestimonial(content) && (
            <div className={styles.testimonialHeader}>
              <div className={styles.relatedProduct}>
                {content.referenced_product?.[0]?.title?.toUpperCase()}
              </div>
              <h1 className={styles.payoff}>{content.payoff}</h1>
              <div>
                <Image
                  item={content}
                  imageField={'icon'}
                  className={styles.avatar}
                />
                <div>{content.person_name}</div>
                <div>{testimonial_info}</div>
                <div className={styles.location}>{content.location}</div>
              </div>
            </div>
          )}
          <RenderBlocks {...props} path={path} />
        </div>
        {!isExpanded && isOverflowing && (
          <Button
            onPress={() => {
              if (window.gtag) {
                window.gtag('event', 'expand_news_testimonial', {
                  event_category: 'engagement',
                  event_label: content.title,
                  value: 1,
                });
              }
              setIsExpanded((v) => !v);
            }}
            className={styles.toggleButton}
            data-open={isExpanded}
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </Button>
        )}
      </div>
      <aside>
        {/* @ts-ignore core mistypings */}
        <SlotRenderer name="blogRightPortlet" content={content} />
      </aside>
    </Container>
  ) : null;
};

export default NewsTestimonialView;
